<template>
  <section v-if="offer.active" class="first-sip" :class="{ 'first-sip--expanded': expanded }" aria-label="First Sip Society offer">
    <div class="first-sip__intro">
      <p class="bj-label first-sip__eyebrow">First Sip Society</p>
      <h1 v-if="expanded" class="bj-h1 first-sip__title">You’re in!</h1>
      <p class="first-sip__reward" role="status">Your <strong>30% off</strong> is {{ offer.isVerified(cart.sessionId) ? 'unlocked' : 'ready to claim' }}.</p>
      <p v-if="expanded" class="bj-body first-sip__lede">Snap it. Post it. Save 30%. A little thank-you for sharing your Banjino’s.</p>
      <BrandButton v-if="expanded" variant="burgundy" to="/products">Shop the offer</BrandButton>
      <p v-if="!offer.isVerified(cart.sessionId)" class="first-sip__terms">Prices show the regular price until your reward is unlocked. You can check out anytime; your 30% applies automatically once you share.</p>
      <p class="first-sip__terms">30% off shop items. One reward per Instagram account, valid for 7 days. Shipping and tax extra. Offers don’t combine. Texas delivery · 21+.</p>
    </div>
    <details class="first-sip__how" :open="expanded">
      <summary>Snap. Tag. Save. <span>How it works</span></summary>
      <ol>
        <li><strong>Capture your first sip.</strong> Take a photo or video with your Banjino’s.</li>
        <li><strong>Share & tag.</strong> Post a public Story, photo or Reel. Mention <a :href="instagramUrl" target="_blank" rel="noopener noreferrer">@drinkbanjinos</a> in your Story, or use Tag people for a photo or Reel.</li>
        <li><strong>Check your DMs. Enjoy 30% off.</strong> Stories trigger a reply. For a photo or Reel, DM us <strong>SIP</strong>. We’ll check your tag and send your reward link.</li>
      </ol>
      <FirstSipVerification v-if="expanded" />
      <p v-else class="first-sip__hint">Open your Instagram reward link to unlock your savings. <router-link to="/">See how it works</router-link>.</p>
    </details>
    <button type="button" class="first-sip__remove" @click="removeOffer">Remove offer</button>
  </section>
  <p v-else-if="offer.linkError" class="first-sip__error" role="alert">{{ offer.linkError }}</p>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router'
import { useOfferStore } from '@/stores/offer'
import { useCartStore } from '@/stores/cart'
import BrandButton from './BrandButton.vue'
import FirstSipVerification from './FirstSipVerification.vue'

defineProps({ expanded: Boolean })
const offer = useOfferStore()
const cart = useCartStore()
const route = useRoute()
const router = useRouter()
const instagramUrl = `https://www.instagram.com/${offer.firstSip.instagramHandle}/`
function removeOffer() {
  offer.clear()
  const { promo, ...query } = route.query
  router.replace({ query })
}
</script>

<style scoped>
.first-sip { position: relative; display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 24px 48px; padding: 28px max(24px, calc((100vw - 1248px) / 2)); background: color-mix(in srgb, var(--bj-green) 8%, var(--bj-bg)); border-bottom: 1px solid var(--bj-tan); color: var(--bj-green-dark); }
.first-sip > * { min-width: 0; }
.first-sip--expanded { padding-top: 52px; padding-bottom: 38px; background: radial-gradient(ellipse at top left, color-mix(in srgb, var(--bj-tan) 35%, transparent), transparent 65%), color-mix(in srgb, var(--bj-green) 8%, var(--bj-bg)); }
.first-sip__eyebrow { color: var(--bj-green); margin: 0 0 10px; }
.first-sip__title { color: var(--bj-orange-darker); font-size: clamp(48px, 6vw, 80px); margin-bottom: 16px; }
.first-sip__reward { font-family: var(--bj-font-body); font-size: 25px; line-height: 1.25; margin: 0; }
.first-sip--expanded .first-sip__reward { font-size: clamp(26px, 3vw, 38px); }
.first-sip__reward strong { color: var(--bj-burgundy); }
.first-sip__lede { max-width: 430px; margin: 14px 0 22px; }
.first-sip__terms, .first-sip__hint { font: 13px/1.6 var(--bj-font-ui); margin: 14px 0 0; max-width: 440px; }
.first-sip__how { align-self: center; font: 16px/1.55 var(--bj-font-body); }
.first-sip__how summary { font: 700 17px/1.5 var(--bj-font-ui); cursor: pointer; }
.first-sip__how summary span { font-size: 12px; font-weight: 400; margin-left: 8px; }
.first-sip__how ol { margin: 18px 0; padding-left: 25px; }
.first-sip__how li { padding-left: 6px; margin: 12px 0; }
.first-sip__how li::marker { color: var(--bj-burgundy); font-weight: 700; }
.first-sip__how a { color: var(--bj-green-dark); text-underline-offset: 3px; }
.first-sip__remove { text-decoration: underline; text-underline-offset: 3px; cursor: pointer; }
.first-sip__remove { justify-self: start; font: 12px/1.4 var(--bj-font-ui); color: var(--bj-green); }
.first-sip :is(a, button, summary):focus-visible { outline: 2px solid var(--bj-burgundy); outline-offset: 5px; }
.first-sip__error { padding: 20px 24px; color: var(--bj-burgundy); background: color-mix(in srgb, var(--bj-tan) 25%, var(--bj-bg)); font: 16px/1.5 var(--bj-font-ui); }
@media (max-width: 700px) {
  .first-sip { grid-template-columns: minmax(0, 1fr); gap: 20px; padding: 24px 20px 18px; }
  .first-sip--expanded { padding-top: 32px; }
  .first-sip__reward { font-size: 24px; }
  .first-sip__how { width: 100%; }
  .first-sip .bj-btn { padding-inline: 24px; white-space: normal; }
}
</style>
